"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { Database } from "@/types/database";
import { UserProfile } from "@/types";
import { notifyError } from "@/lib/bugsnag";

type UserRole = Database["public"]["Enums"]["user_role"];

interface UserWithRole extends User {
  role?: UserRole;
  fullName?: string;
  clinicId?: string;
  clinicName?: string;
}

interface AuthContextType {
  user: UserWithRole | null;
  isLoading: boolean;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);
  const router = useRouter();
  const pathname = usePathname();

  // Role-based routing logic
  useEffect(() => {
    if (!isLoading && user?.role && pathname === "/") {
      router.push(`/${user.role}`);
    }
  }, [user, isLoading, pathname, router]);

  useEffect(() => {
    let isMounted = true;
    let initialLoadComplete = false;

    const fetchUserWithRole = async (authUser: User): Promise<UserWithRole> => {
      // Extract role from metadata immediately - this is our "local" source of truth
      const metadataRole = authUser.app_metadata?.role || authUser.user_metadata?.role;
      
      try {
        // Use RPC function that bypasses RLS to avoid infinite recursion
        // We avoid .maybeSingle() / .single() to prevent 406 errors in some environments
        const { data, error } = await supabase.rpc("get_current_user_profile");

        if (error) {
          console.error("[AuthProvider] Profile fetch error:", error);
          notifyError(error, {
            source: "AuthProvider",
            action: "fetchUserWithRole",
          });
          // Fallback to metadata - satisfies "local check" requirement
          return { ...authUser, role: metadataRole || "clinic" } as UserWithRole;
        }

        // RPC returns an array for TABLE functions
        const userData = (Array.isArray(data) ? data[0] : data) as UserProfile | null;

        if (!userData) {
          console.warn(`[AuthProvider] No profile found in DB for user ${authUser.id}`);
          return { ...authUser, role: metadataRole || "clinic" } as UserWithRole;
        }

        return {
          ...authUser,
          role: userData.role || metadataRole || "clinic",
          fullName: userData.full_name,
          clinicId: userData.clinic_id,
          clinicName: userData.clinic_name,
        } as UserWithRole;
      } catch (err) {
        console.error("[AuthProvider] Unexpected error in AuthProvider:", err);
        notifyError(err, {
          source: "AuthProvider",
          action: "fetchUserWithRole",
        });
        return { ...authUser, role: metadataRole || "clinic" } as UserWithRole;
      }
    };

    const getInitialSession = async () => {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();

        if (!isMounted) return;

        if (authUser) {
          const metadataRole = authUser.app_metadata?.role || authUser.user_metadata?.role;
          if (metadataRole && isMounted) {
            setUser({ ...authUser, role: metadataRole } as UserWithRole);
          }

          const userWithRole = await fetchUserWithRole(authUser);
          if (isMounted) setUser(userWithRole);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("[AuthProvider] Error fetching session:", error);
        notifyError(error, {
          source: "AuthProvider",
          action: "getInitialSession",
        });
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          initialLoadComplete = true;
        }
      }
    };

    getInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Skip if initial load hasn't completed to avoid race condition
      if (!initialLoadComplete) return;

      if (!isMounted) return;

      if (event === "SIGNED_OUT") {
        setUser(null);
        return;
      }

      supabase.auth.getUser().then(({ data: { user: authUser } }) => {
        if (!isMounted) return;
        if (!authUser) {
          setUser(null);
          return;
        }

        const metadataRole = authUser.app_metadata?.role || authUser.user_metadata?.role;
        setUser({ ...authUser, role: metadataRole || "clinic" } as UserWithRole);

        fetchUserWithRole(authUser).then((userWithRole) => {
          if (isMounted) setUser(userWithRole);
        });
      });
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signOut = () => {
    // Clear auth cookies immediately (synchronous)
    document.cookie.split(";").forEach(c => {
      const name = c.trim().split("=")[0];
      if (name.startsWith("sb-") || name.includes("supabase")) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });

    // Fire and forget - let Supabase clean up in background
    supabase.auth.signOut({ scope: "local" });

    // Redirect immediately
    window.location.replace("/auth/signin");
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
